import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import {
  AccuracyReportFiltersSummary,
} from '../components/AccuracyReportFiltersSummary';
import { AccuracyMetricsSection } from '../components/AccuracyMetricsSection';
import {
  buildDrilldownPath,
  buildTuningDrilldownPath,
  type MetricCountDrilldown,
} from '../services/accuracyCountDrilldown';
import {
  getBaselineReport as getBaselineReportApi,
  listTuningSessions as listTuningSessionsApi,
  listEvaluationRuns as listEvaluationRunsApi,
} from '../services/accuracyBackendApi';
import { formatAccuracyStatusLabel } from '../services/accuracyStatusLabels';
import { buildTuningDraftPath } from '../services/accuracyReportPaths';
import { useAccuracyScopeMetadata } from '../services/accuracyScopeMetadata';
import type { EvaluationHistoryRow } from '../services/accuracyEvaluationHistory';
import type { TuningSession } from '../types/accuracy';
import './AccuracyReportPage.css';
import './AccuracyTuningPages.css';

const AccuracyReportDetailPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const scopeMetadata = useAccuracyScopeMetadata();
  const [refresh, setRefresh] = useState(0);
  const [apiReport, setApiReport] = useState<any>(undefined);
  const [apiTuningSessions, setApiTuningSessions] = useState<TuningSession[]>([]);
  const [apiTuningRunsBySession, setApiTuningRunsBySession] = useState<
    Record<string, Awaited<ReturnType<typeof listEvaluationRunsApi>>>
  >({});

  const report = useMemo(
    () => {
      if (!reportId) return undefined;
      return apiReport;
    },
    [reportId, refresh, apiReport]
  );

  const bump = useCallback(() => setRefresh((n) => n + 1), []);

  useEffect(() => {
    if (!reportId) return;
    (async () => {
      try {
        const r = await getBaselineReportApi(reportId);
        setApiReport(r);
      } catch {
        setApiReport(undefined);
      }
      try {
        const sessions = (await listTuningSessionsApi()).filter(
          (x) => x.baseline_report_id === reportId
        );
        sessions.sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
        setApiTuningSessions(sessions);
        const runsBySession: Record<string, Awaited<ReturnType<typeof listEvaluationRunsApi>>> =
          {};
        for (const s of sessions) {
          runsBySession[s.id] = await listEvaluationRunsApi(s.id);
        }
        setApiTuningRunsBySession(runsBySession);
      } catch {
        setApiTuningSessions([]);
        setApiTuningRunsBySession({});
      }
    })();
  }, [reportId, refresh]);

  useEffect(() => {
    if (!reportId || !report) return;
    if (report.status !== 'queued' && report.status !== 'running') return;

    const timer = window.setInterval(bump, 500);
    return () => window.clearInterval(timer);
  }, [reportId, report?.status, bump]);

  const tuningSessions = apiTuningSessions;

  const historyRows = useMemo((): EvaluationHistoryRow[] => {
    if (!reportId || !report) return [];
    const rows: EvaluationHistoryRow[] = [];
    rows.push({
      id: `baseline:${report.id}`,
      kind: 'baseline',
      label: report.name,
      context: 'ベースライン評価',
      status: report.status,
      created_at: report.created_at,
      result: report.result,
      baseline_report_id: report.id,
    });
    for (const session of tuningSessions) {
      const runs = apiTuningRunsBySession[session.id] ?? [];
      for (const r of runs) {
        rows.push({
          id: `tuning:${r.id}`,
          kind: 'tuning',
          label: session.name,
          status: r.status,
          created_at: r.created_at,
          result: r.result,
          baseline_report_id: report.id,
          tuning_session_id: session.id,
          evaluation_run_id: r.id,
          run_number: r.run_number,
        });
      }
      if (runs.length === 0) {
        rows.push({
          id: `tuning-session:${session.id}`,
          kind: 'tuning',
          label: session.name,
          context: 'ドラフト作成済み · 評価未実行',
          status: 'active',
          created_at: session.created_at,
          baseline_report_id: report.id,
          tuning_session_id: session.id,
        });
      }
    }
    return rows;
  }, [reportId, report, tuningSessions, apiTuningRunsBySession]);

  const result = report?.result;

  const warnings = useMemo(() => {
    if (!result) return [];
    const w: string[] = [];
    if (result.url_count < 30) w.push('サンプル数が少ないため、指標の信頼性が低い可能性があります。');
    if ((result.legacy_violation_count || 0) > 0) {
      w.push('一部の違反は検出時ボックス未保存のため、誤バウンディングは過小評価されます。');
    }
    return w;
  }, [result]);

  const isComputing = report?.status === 'running' || report?.status === 'queued';

  const handleCountClickForRow = useCallback(
    (row: EvaluationHistoryRow, drilldown: MetricCountDrilldown) => {
      if (row.kind === 'tuning' && row.tuning_session_id && row.evaluation_run_id) {
        navigate(
          buildTuningDrilldownPath({
            evaluationRunId: row.evaluation_run_id,
            tuningSessionId: row.tuning_session_id,
            drilldown,
          })
        );
        return;
      }
      const baselineId = row.baseline_report_id || reportId;
      if (!baselineId) return;
      navigate(buildDrilldownPath({ reportId: baselineId, drilldown }));
    },
    [navigate, reportId]
  );

  const handleStartTuning = useCallback(() => {
    if (!report || report.status !== 'completed' || !reportId) return;
    navigate(buildTuningDraftPath(reportId));
  }, [report, reportId, navigate]);

  const tuningHeaderAction = (
    <button type="button" className="accuracy-button primary" onClick={handleStartTuning}>
      <Plus size={16} style={{ marginRight: 6 }} />
      チューニング評価行を追加
    </button>
  );

  if (!reportId) {
    return (
      <div className="accuracy-page">
        <Link to="/accuracy" className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          評価レポート
        </Link>
        <div className="accuracy-card accuracy-empty">レポート ID が指定されていません。</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="accuracy-page">
        <Link to="/accuracy" className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          評価レポート
        </Link>
        <div className="accuracy-card accuracy-empty">レポートが見つかりません。</div>
      </div>
    );
  }

  return (
    <div className="accuracy-page accuracy-report-detail-page">
      <Link to="/accuracy" className="accuracy-drilldown-back">
        <ArrowLeft size={16} />
        評価レポート
      </Link>

      <header className="accuracy-header" style={{ marginTop: 12 }}>
        <div className="accuracy-title">
          <h1>{report.name}</h1>
          <p>
            <span className={`accuracy-status ${report.status}`}>
              {formatAccuracyStatusLabel(report.status)}
            </span>
            {' · '}
            {new Date(report.created_at).toLocaleString('ja-JP')}
          </p>
        </div>
      </header>

      {isComputing && (
        <div className="accuracy-card accuracy-empty">
          <Loader2 size={32} className="spin" />
          <p style={{ marginTop: 12 }}>精度を計算しています…</p>
        </div>
      )}

      {report.status === 'failed' && (
        <div className="accuracy-card accuracy-warnings">
          計算に失敗しました: {report.error_message}
        </div>
      )}

      {report.status === 'completed' && result && (
        <>
          <div className="accuracy-card">
            <h3 style={{ marginTop: 0 }}>レポート作成時の評価条件</h3>
            <AccuracyReportFiltersSummary report={report} metadata={scopeMetadata} />
          </div>

          {warnings.length > 0 && (
            <div className="accuracy-card accuracy-warnings">
              {warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}

          <AccuracyMetricsSection
            historyRows={historyRows}
            onCountClickForRow={handleCountClickForRow}
            sectionDescription={
              tuningSessions.length > 0
                ? 'ベースライン評価とチューニング評価を 1 つの表に表示します。右上のボタンから新しいチューニングセッションを追加できます。'
                : 'ベースライン評価とチューニング評価を 1 つの表に表示します。右上のボタンから、ガイドライン調整用の新しい評価行を追加できます。'
            }
            headerAction={tuningHeaderAction}
          />
        </>
      )}
    </div>
  );
};

export default AccuracyReportDetailPage;
